import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CalendarDays, ArrowRight } from 'lucide-react';
import { COHORT_COURSES } from '@/data/catalogue-stats.js';

/**
 * Home page band for the current promoted cohort.
 *
 * Replaces the former "Proven Results" section, which asserted four invented
 * metrics. Everything here is read from the course data — dates, prices, promo
 * codes — so it cannot state anything the course pages do not, and it needs no
 * maintenance when the campaign changes.
 *
 * SELF-RETIRING: sessions are filtered against the clock on every render, and
 * the whole section returns null once every session has passed. That is why
 * scripts/generate-catalogue-stats.js emits raw ISO strings rather than a
 * pre-filtered list — filtering at build time would leave a finished cohort
 * advertised on the home page until someone happened to redeploy.
 *
 * To promote a different cohort: add a `waitlist` block and future `sessions` to
 * the relevant course files. Nothing here is hardcoded to September 2026.
 */

const SYDNEY = 'Australia/Sydney';

function nextSession(isoDates) {
  const now = Date.now();
  const upcoming = isoDates
    .map((s) => new Date(s))
    .filter((d) => !Number.isNaN(d.getTime()) && d.getTime() > now)
    .sort((a, b) => a - b);
  return upcoming[0] ?? null;
}

function formatDate(date) {
  return date.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: SYDNEY,
  });
}

function formatTime(date) {
  return date
    .toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: SYDNEY,
    })
    .replace(/\s/g, '')
    .toLowerCase();
}

/** Price line. A 100%-off promo is shown as "Free with CODE", not "$0". */
function priceLabel({ priceAud, promoCode, promoPercentOff }) {
  if (priceAud === null || priceAud === undefined) return null;
  if (promoCode && promoPercentOff === 100) {
    return { headline: 'Free', note: `with code ${promoCode}`, was: `$${priceAud}` };
  }
  if (promoCode && promoPercentOff > 0) {
    const discounted = priceAud * (1 - promoPercentOff / 100);
    // Whole dollars where the maths gives one, otherwise cents — $296.25 must
    // not render as $296, because that is not what Stripe charges.
    const shown = Number.isInteger(discounted) ? discounted : discounted.toFixed(2);
    return { headline: `$${shown}`, note: `with code ${promoCode}`, was: `$${priceAud}` };
  }
  return { headline: `$${priceAud}`, note: null, was: null };
}

const CohortBand = () => {
  const live = COHORT_COURSES.map((c) => ({ ...c, next: nextSession(c.sessions) })).filter(
    (c) => c.next,
  );

  // Cohort finished, or none configured — render nothing rather than an empty shell.
  if (live.length === 0) return null;

  const label = live.find((c) => c.cohortLabel)?.cohortLabel ?? 'Next cohort';
  const lead = live[0];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#422680] via-[#341671] to-[#280659] text-white w-full overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/30 px-4 py-1.5 text-sm font-semibold tracking-wide mb-5">
            <CalendarDays size={16} />
            {label}
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-white to-[#F54952] bg-clip-text text-transparent">
              AI training that starts with a free evening
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Live online, Monday to Thursday evenings. Start with the {lead.isFree || lead.promoPercentOff === 100 ? 'free' : 'introductory'} session,
            then take the four-hour course built for your role.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {live.map((course, index) => {
            const price = priceLabel(course);
            const isLead = index === 0;
            return (
              <motion.div
                key={course.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className={`relative flex flex-col rounded-2xl p-6 bg-white/10 backdrop-blur-sm border transition-colors hover:bg-white/15 ${
                  isLead ? 'border-[#F54952] border-2' : 'border-white/15'
                }`}
              >
                {isLead && (
                  <div className="absolute -top-3 left-6 bg-[#F54952] text-white text-xs font-bold px-3 py-1 rounded-full">
                    Start here
                  </div>
                )}

                <h3 className="text-lg font-bold leading-snug mb-2">{course.title}</h3>
                {course.audience && (
                  <p className="text-sm text-gray-300 mb-5 flex-1">{course.audience}</p>
                )}

                <div className="flex items-center gap-2 text-sm text-gray-200 mb-4">
                  <CalendarDays size={15} className="shrink-0" />
                  <span>
                    {formatDate(course.next)} · {formatTime(course.next)}
                  </span>
                </div>

                {price && (
                  <div className="mb-5">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold">{price.headline}</span>
                      {price.was && (
                        <span className="text-sm text-gray-400 line-through">{price.was}</span>
                      )}
                    </div>
                    {price.note && (
                      <div className="text-xs text-[#F54952] font-semibold mt-1">{price.note}</div>
                    )}
                  </div>
                )}

                <Link
                  to={`/training/${course.slug}`}
                  className={`mt-auto inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-semibold text-sm transition-colors ${
                    isLead
                      ? 'bg-[#F54952] hover:bg-[#d93f47] text-white'
                      : 'bg-white/15 hover:bg-white/25 text-white'
                  }`}
                >
                  View course
                  <ArrowRight size={16} />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CohortBand;
